import { API } from "aws-amplify";

import { ClientPlatform } from "@/lib/models/assist";

/**
 * nola-web-api の校正系 API クライアント。
 *
 * - エンドポイント基底は `NolaRestAPI`（`src/initializer.ts` で `API.configure` 済み）。
 *   IAM 認証 + Cognito Identity Pool で SigV4 署名込みで呼ぶ。
 * - サーバー側の仕様は `nola-backend/api/src/nola-web-api/proofreading-apis.ts` と一致。
 * - 詳細インターフェースは `docs/proofreading/backend-api.md` を参照。
 *
 * 本ファイルはルールベース校正に関わる薄い呼び出しのみを担当する。
 * 校正結果のドメイン変換等は store / コンポーネント側に閉じ込める。
 */

/** Assist と同様、Web クライアントは WEB 固定（Capacitor 未使用）。 */
export const PROOFREADING_CLIENT_PLATFORM = ClientPlatform.WEB;

/* ---- ルール一覧 API（GET /proofreading/rules） ---- */

/** UI 上のグルーピング用カテゴリ ID（バックエンドと同一）。 */
export type ProofreadingRuleCategoryId =
  | "brackets_and_indent"
  | "punctuation_symbols"
  | "characters_and_numbers"
  | "sentence_structure"
  | "expressions"
  | "variation"
  | "text_integrity";

/** 1 ルール分のメタデータ（API レスポンスの `rules[]` 要素）。 */
export interface ProofreadingRuleEntry {
  rule: string;
  label: string;
  category: ProofreadingRuleCategoryId;
  description: string;
}

/** カテゴリ ID と表示名のペア（API レスポンスの `categories[]` 要素）。 */
export interface ProofreadingCategoryEntry {
  category: ProofreadingRuleCategoryId;
  label: string;
}

/** `GET /proofreading/rules` のレスポンス全体。 */
export interface ProofreadingRulesResponse {
  lang: "ja";
  rules: ProofreadingRuleEntry[];
  categories: ProofreadingCategoryEntry[];
}

/** サポート言語コード（現状は ja のみ。サーバ側もそれ以外を渡すと 422）。 */
export type ProofreadingRulesLang = "ja";

/* ---- ルールベース校正（POST /proofreading/rule-based） ---- */
/* eslint-disable camelcase -- API レスポンス型はバックエンドの snake_case に合わせる */

export type RuleBasedErrorPriority = "low" | "middle" | "high";

/** ルールベース校正の個別指摘（位置情報は 1 始まり）。 */
export interface RuleBasedErrorEntry {
  manuscript_id: string;
  start_line: number;
  start_word: number;
  end_line: number;
  end_word: number;
  length: number;
  target_text: string;
  error_code: string;
  priority: RuleBasedErrorPriority;
  fix_text: string | null;
  detail?: string;
}

export interface VariationSummaryPosition {
  manuscript_id: string;
  start_line: number;
  start_word: number;
  end_line: number;
  end_word: number;
  length: number;
}

export interface VariationSummaryVariant {
  text: string;
  count: number;
  position?: VariationSummaryPosition[];
}

export type VariationSummaryType =
  | "okurigana_variation"
  | "kanji_hiraki_variation"
  | "verb_variation"
  | "number_style_variation"
  | "politeness_style_variation";

export interface VariationSummaryItem {
  variation_type: VariationSummaryType;
  canonical: string;
  variants: VariationSummaryVariant[];
  total_count: number;
  manuscript_ids: string[];
}

export type RuleBasedManuscriptSource = "work" | "content";

export interface PostRuleBasedRequest {
  novelId: string;
  manuscripts: Array<{ manuscript_key: string; source?: RuleBasedManuscriptSource }>;
  /** NolaCoin 課金プラットフォーム。省略時サーバは WEB 扱い。 */
  platform?: ClientPlatform;
  options?: {
    checks?: Record<string, boolean>;
  };
}

export interface PostRuleBasedResponse {
  status: "ok";
  novelId: string;
  results: RuleBasedErrorEntry[];
  variation_summary: {
    items: VariationSummaryItem[];
  };
  /** このリクエストで実際に引いた Fish（無料再実行は 0。差額時は差額） */
  consumedFish?: number;
  /** 今回の文字数に対する段階制必要額 */
  requiredFish?: number;
  /** セッション累計支払額（更新後） */
  sessionUsedFish?: number;
  /** 今回 subCoin したか */
  chargedThisRequest?: boolean;
}

/* ---- Fish 見積り（POST /proofreading/estimate-fish） ---- */

export interface EstimateProofreadingFishParams {
  novelId: string;
  manuscripts: PostRuleBasedManuscriptParam[];
}

export interface EstimateProofreadingFishManuscript {
  manuscript_key: string;
  source: RuleBasedManuscriptSource;
  chars: number;
}

export interface EstimateProofreadingFishResponse {
  status: "ok";
  novelId: string;
  totalChars: number;
  /** 段階制 Fish。上限超過時は null */
  estimatedFish: number | null;
  withinLimit: boolean;
  maxChars: number;
  manuscripts: EstimateProofreadingFishManuscript[];
  /**
   * 有効な支払い済みセッションの累計 Fish。
   * なし / 期限切れは 0。旧 API では未返却のため optional。
   */
  sessionUsedFish?: number;
}

/* ---- 確定適用（POST /proofreading/apply） ---- */

export type ProofreadingApplyErrorCode =
  | "WORK_NOT_FOUND"
  | "MANUSCRIPT_META_NOT_FOUND"
  | "S3_PUT_FAILED"
  | "INTERNAL_ERROR";

export interface ProofreadingApplyAppliedItem {
  manuscriptKey: string;
  savedAt: number;
  characterCount: number;
  versionId: string | null;
}

export interface ProofreadingApplyFailedItem {
  manuscriptKey: string;
  error_code: ProofreadingApplyErrorCode;
  message: string;
}

export interface ProofreadingApplyCleanupWarning {
  manuscriptKey: string;
  message: string;
}

export interface ProofreadingApplyResponse {
  status: "ok";
  novelId: string;
  applied: ProofreadingApplyAppliedItem[];
  failed: ProofreadingApplyFailedItem[];
  cleanupWarnings: ProofreadingApplyCleanupWarning[];
  summary: {
    total: number;
    succeeded: number;
    failed: number;
  };
}

export interface PutProofreadingWorkResponse {
  status: "ok";
  novelId: string;
  manuscriptId: string;
  size: number;
  updatedAt: string;
}

/** `GET /proofreading/work/:novelId` のレスポンス（`withMetadata` なし）。 */
export interface ProofreadingWorkListResponse {
  status: "ok";
  novelId: string;
  manuscriptIds: string[];
}

/** `DELETE /proofreading/work/:novelId` のレスポンス。 */
export interface ProofreadingWorkDeleteResponse {
  status: "ok";
  novelId: string;
  deleted: number;
}

function extractPlainTextResponse(response: unknown): string | null {
  if (response == null) return null;
  if (typeof response === "string") return response;
  if (typeof response === "object") {
    const payload = response as {
      body?: unknown;
      text?: unknown;
      data?: unknown;
      statusCode?: unknown;
    };
    if (typeof payload.body === "string") return payload.body;
    if (typeof payload.text === "string") return payload.text;
    if (typeof payload.data === "string") return payload.data;
  }
  return null;
}

function isValidPlainTextExtraction(text: string | null): text is string {
  if (text == null) return false;
  return text !== "[object Object]";
}

function isWorkManuscriptNotFoundError(error: unknown): boolean {
  if (error == null || typeof error !== "object") return false;
  const err = error as { response?: { status?: number }; message?: string; code?: string };
  if (err.response?.status === 404) return true;
  if (err.code === "NotFoundException") return true;
  const message = err.message ?? "";
  return message.includes("404") || message.includes("status code 404");
}

/**
 * 校正ルール一覧を取得する。
 *
 * - 順序はバックエンドの `ALL_RULE_CHECK_TYPES` の宣言順を保持して返る（UI 表示順）。
 * - `lang` 未指定時はサーバ側で `ja` フォールバック。
 *
 * @throws Amplify API のレスポンスエラー（422 = 未対応 lang、500 = サーバーエラー）
 */
export async function getProofreadingRules(lang?: ProofreadingRulesLang): Promise<ProofreadingRulesResponse> {
  const response = await API.get("NolaRestAPI", "/proofreading/rules", {
    queryStringParameters: lang ? { lang } : undefined,
  });

  if (!response || !Array.isArray(response.rules) || !Array.isArray(response.categories)) {
    throw new Error("ルール一覧 API のレスポンス形式が不正です。");
  }

  return response as ProofreadingRulesResponse;
}

export interface PostRuleBasedManuscriptParam {
  manuscriptKey: string;
  /** Work 上に存在する原稿は `"work"`、存在しない原稿は `"content"` を付与する。 */
  source?: RuleBasedManuscriptSource;
}

export interface PostRuleBasedProofreadingParams {
  novelId: string;
  manuscripts: PostRuleBasedManuscriptParam[];
  selectedRuleIds: string[];
  allRuleIds: string[];
  /** 省略時は WEB */
  platform?: ClientPlatform;
  /** 固有名詞除外（未指定時はサーバ既定: 登録済みをマージ。誤記系・注意語・表記揺れ） */
  useRegisteredProperNouns?: boolean;
  properNouns?: string[];
}

/**
 * ルールベース校正を実行する（`POST /proofreading/rule-based`）。
 * 受付時に段階制 Fish を課金する（有効 PAID セッション内は無料／差額あり）。
 */
export async function postRuleBasedProofreading(
  params: PostRuleBasedProofreadingParams
): Promise<PostRuleBasedResponse> {
  const selectedSet = new Set(params.selectedRuleIds);
  const checks: Record<string, boolean> = {};
  params.allRuleIds.forEach((ruleId) => {
    checks[ruleId] = selectedSet.has(ruleId);
  });

  const response = await API.post("NolaRestAPI", "/proofreading/rule-based", {
    headers: {
      "Content-Type": "application/json",
    },
    body: {
      novelId: params.novelId,
      manuscripts: params.manuscripts.map(({ manuscriptKey, source }) => ({
        manuscript_key: manuscriptKey,
        ...(source ? { source } : {}),
      })),
      platform: params.platform ?? PROOFREADING_CLIENT_PLATFORM,
      options: {
        checks,
        ...(typeof params.useRegisteredProperNouns === "boolean"
          ? { useRegisteredProperNouns: params.useRegisteredProperNouns }
          : {}),
        ...(params.properNouns && params.properNouns.length > 0
          ? { properNouns: params.properNouns }
          : {}),
      },
    },
  });

  if (!response || response.status !== "ok" || !Array.isArray(response.results) || !response.variation_summary) {
    throw new Error("校正実行 API のレスポンス形式が不正です。");
  }

  return response as PostRuleBasedResponse;
}

/**
 * 選択原稿の文字数と段階制 Fish 見積りを返す（`POST /proofreading/estimate-fish`）。
 * 課金は行わない。
 */
export async function estimateProofreadingFish(
  params: EstimateProofreadingFishParams,
): Promise<EstimateProofreadingFishResponse> {
  const response = await API.post("NolaRestAPI", "/proofreading/estimate-fish", {
    headers: {
      "Content-Type": "application/json",
    },
    body: {
      novelId: params.novelId,
      manuscripts: params.manuscripts.map(({ manuscriptKey, source }) => ({
        manuscript_key: manuscriptKey,
        ...(source ? { source } : {}),
      })),
    },
  });

  if (
    !response
    || response.status !== "ok"
    || typeof response.totalChars !== "number"
    || typeof response.withinLimit !== "boolean"
    || typeof response.maxChars !== "number"
    || !Array.isArray(response.manuscripts)
  ) {
    throw new Error("Fish 見積り API のレスポンス形式が不正です。");
  }

  return response as EstimateProofreadingFishResponse;
}

/**
 * 作業中原稿の一覧を取得する（`GET /proofreading/work/:novelId`）。
 */
export async function listProofreadingWorkManuscripts(
  novelId: string,
): Promise<ProofreadingWorkListResponse> {
  const response = await API.get(
    "NolaRestAPI",
    `/proofreading/work/${encodeURIComponent(novelId)}`,
    {},
  );

  if (!response || response.status !== "ok" || !Array.isArray(response.manuscriptIds)) {
    throw new Error("作業中原稿一覧 API のレスポンス形式が不正です。");
  }

  return response as ProofreadingWorkListResponse;
}

/**
 * 作業中原稿を一括削除する（`DELETE /proofreading/work/:novelId`）。
 */
export async function deleteProofreadingWork(novelId: string): Promise<ProofreadingWorkDeleteResponse> {
  const response = await API.del(
    "NolaRestAPI",
    `/proofreading/work/${encodeURIComponent(novelId)}`,
    {},
  );

  if (!response || response.status !== "ok") {
    throw new Error("作業中原稿削除 API のレスポンス形式が不正です。");
  }

  return response as ProofreadingWorkDeleteResponse;
}

/**
 * 作業中原稿を取得する（`GET /proofreading/work/:novelId/:manuscriptId`）。
 * 未存在時は 404 または `null` レスポンスとなり、`{ exists: false }` を返す。
 *
 * 新形式は JSON `{ text, work_progress_data }`。旧 text/plain もフォールバックで解釈する。
 */
export type ProofreadingWorkManuscriptGetResult =
  | { exists: true; text: string; workProgressData: string | null }
  | { exists: false };

function extractWorkProgressData(payload: {
  work_progress_data?: unknown;
}): string | null {
  return typeof payload.work_progress_data === "string" ? payload.work_progress_data : null;
}

function extractWorkManuscriptPayload(
  response: unknown,
): { text: string; workProgressData: string | null } | null {
  if (response == null) return null;

  if (typeof response === "object") {
    const payload = response as {
      text?: unknown;
      work_progress_data?: unknown;
      body?: unknown;
      data?: unknown;
    };

    if (typeof payload.text === "string" && isValidPlainTextExtraction(payload.text)) {
      return {
        text: payload.text,
        workProgressData: extractWorkProgressData(payload),
      };
    }

    const wrapped = extractPlainTextResponse(response);
    if (isValidPlainTextExtraction(wrapped)) {
      // Amplify が text/plain を body/data に入れた場合、または JSON 文字列を入れた場合
      return extractWorkManuscriptPayload(wrapped);
    }

    return null;
  }

  if (typeof response === "string") {
    const trimmed = response.trim();
    if (trimmed.startsWith("{")) {
      try {
        const parsed: unknown = JSON.parse(response);
        const fromJson = extractWorkManuscriptPayload(parsed);
        if (fromJson != null) return fromJson;
      } catch {
        // plain text として扱う
      }
    }
    if (isValidPlainTextExtraction(response)) {
      return { text: response, workProgressData: null };
    }
  }

  return null;
}

export async function getProofreadingWorkManuscript(
  novelId: string,
  manuscriptId: string,
): Promise<ProofreadingWorkManuscriptGetResult> {
  try {
    // JSON `{ text, work_progress_data }` を優先。text/plain 時は Amplify が string / body で返すことがある。
    const response = await API.get(
      "NolaRestAPI",
      `/proofreading/work/${encodeURIComponent(novelId)}/${encodeURIComponent(manuscriptId)}`,
      {
        responseType: "text",
      },
    );

    const extracted = extractWorkManuscriptPayload(response);
    if (extracted != null) {
      return {
        exists: true,
        text: extracted.text,
        workProgressData: extracted.workProgressData,
      };
    }

    return { exists: false };
  } catch (error) {
    if (isWorkManuscriptNotFoundError(error)) {
      return { exists: false };
    }
    throw error;
  }
}

/**
 * 作業中原稿を保存する（`POST /proofreading/work/:novelId/:manuscriptId`）。
 *
 * 旧仕様は `PUT` ＋ `text/plain` の生ボディだったが、Amplify v1 が body を必ず
 * `JSON.stringify` してしまい本文が二重エンコードされる問題と、PUT メソッド固有の通信不調を
 * 避けるため、`POST` ＋ JSON ボディ `{ text, work_progress_data }` に変更した。
 */
export async function postProofreadingWorkManuscript(
  novelId: string,
  manuscriptId: string,
  text: string,
  workProgressData: string,
): Promise<PutProofreadingWorkResponse> {
  const response = await API.post(
    "NolaRestAPI",
    `/proofreading/work/${encodeURIComponent(novelId)}/${encodeURIComponent(manuscriptId)}`,
    {
      headers: {
        "Content-Type": "application/json",
      },
      body: {
        text,
        // eslint-disable-next-line camelcase -- API 仕様のキー名
        work_progress_data: workProgressData,
      },
    }
  );

  if (!response || response.status !== "ok") {
    throw new Error("作業中原稿の保存 API のレスポンス形式が不正です。");
  }

  return response as PutProofreadingWorkResponse;
}

/**
 * 作業中原稿を正規原稿に確定適用する（`POST /proofreading/apply`）。
 */
export async function postProofreadingApply(novelId: string): Promise<ProofreadingApplyResponse> {
  const response = await API.post("NolaRestAPI", "/proofreading/apply", {
    headers: {
      "Content-Type": "application/json",
    },
    body: { novelId },
  });

  if (!response || response.status !== "ok" || !response.summary) {
    throw new Error("確定適用 API のレスポンス形式が不正です。");
  }

  return response as ProofreadingApplyResponse;
}

/* eslint-enable camelcase */
